import {
  LegalServiceCategory,
  LegalServiceContent,
  LegalServiceGroups,
  Service,
  ServiceRequestInternalType,
  ServiceRequestStatus,
} from '@/utils/service-request/types'

import {routeTo} from '../router'

export const useInCommerceInternalTypes = [
  ServiceRequestInternalType.USStatementOfUse,
  ServiceRequestInternalType.SoUForPartner,
  ServiceRequestInternalType.USDeclarationOfUse,
  ServiceRequestInternalType.DoUForPartner,
]

export const serviceNameToLabel: Record<Service, string> = {
  [Service.tmStrategy]: 'Trademark strategy',
  [Service.extraChecksPackage1]: "5 additional lawyer's checks",
  [Service.extraChecksPackage2]: "10 additional lawyer's checks",
  [Service.tmRepresentation]: 'Trademark representation',
  [Service.tmAssignment]: 'Trademark assignment',
  [Service.tmRenewal]: 'Trademark renewal',
  [Service.statementOfUse]: 'Statement of use',
  [Service.gsLimitation]: 'Trademark limitation',
  [Service.tmAssignmentAgreement]: 'Assignment agreement',
  [Service.officeAction]: 'Office action',
  [Service.responseToObjection]: 'Objection',
  [Service.coexistenceAgreement]: 'Coexistence',
  [Service.cdLetter]: 'Cease&desist letter',
  [Service.cdLetterResponse]: 'Cease&desist letter response',
  [Service.tmSettlement]: 'Trademark settlement',
  [Service.domainResolution]: 'Domain resolution',
  [Service.b2bPartnership]: 'B2B partnership',
  [Service.tmWatch]: 'TM watch',
  [Service.extraClasses]: 'Extra classes',
  [Service.nonStandardCountryRegistration]: 'Registration in countries from extended list',
  [Service.customGSList]: 'Custom goods&services list',
  [Service.e2eProtection]: 'E2E protection',
  [Service.filingOpposition]: 'Filing opposition',
  [Service.enforcementOfRights]: 'Enforcement of rights',
  [Service.declarationOfIncontestability]: 'Declaration of incontestability',
  [Service.monitoringCaD]: 'C&D based on monitoring result',
  [Service.monitoringOpposition]: 'Opposition based on monitoring result',
  [Service.officeActionDeadlineExtension]: 'Office action deadline extension',
  [Service.declarationOfUse]: 'Declaration of use',
  [Service.administrativeChange]: 'Administrative change',
  [Service.monitoringMarketplaceCaD]: 'C&D letter & take down (marketplace)',
  [Service.monitoringSocialMediaCaD]: 'C&D letter & take down (social media)',
  [Service.warningLetter]: 'Warning letter',
  [Service.caseSolution]: 'Case solution',
  [Service.infringementTakedown]: 'Infringement takedown',
  // only here for completeness as setting typing without it is pain
  [Service.tmRegistration]: 'Trademark registration',
  [Service.brandMonitoring]: 'Brand monitoring',
}

export const serviceNameToTitle: Record<Service, string> = {
  [Service.tmStrategy]: 'legal-services-tm-strategy-title',
  [Service.extraChecksPackage1]: 'legal-services-extra-checks-package-1',
  [Service.extraChecksPackage2]: 'legal-services-extra-checks-package-2',
  [Service.tmRepresentation]: 'legal-services-tm-representation-title',
  [Service.tmAssignment]: 'legal-services-tm-assignment-title',
  [Service.tmRenewal]: 'legal-services-tm-renewal-title',
  [Service.statementOfUse]: 'legal-services-tm-statement-of-use-title',
  [Service.gsLimitation]: 'legal-services-tm-limitation-title',
  [Service.tmAssignmentAgreement]: 'legal-services-ownership-transfer-title',
  [Service.officeAction]: 'legal-services-tm-office-action-title',
  [Service.responseToObjection]: 'legal-services-tm-objection-title',
  [Service.coexistenceAgreement]: 'legal-services-tm-coexistence-title',
  [Service.cdLetter]: 'legal-services-tm-c&d-title',
  [Service.cdLetterResponse]: 'legal-services-tm-c&d-response-title',
  [Service.tmSettlement]: 'legal-services-tm-settlement-title',
  [Service.domainResolution]: 'service-domain-resolution-title',
  [Service.b2bPartnership]: 'service-b2b-partnership-title',
  [Service.tmWatch]: 'service-tm-watch-title',
  [Service.extraClasses]: 'service-extra-classes-title',
  [Service.nonStandardCountryRegistration]: 'service-non-standard-country-registration-title',
  [Service.customGSList]: 'service-custom-gs-list-title',
  [Service.e2eProtection]: 'service-e2e-protection-title',
  [Service.filingOpposition]: 'legal-services-filing-opposition-title',
  [Service.enforcementOfRights]: 'legal-services-enforcement-of-rights-title',
  [Service.declarationOfIncontestability]:
    'legal-services-tm-declaration-of-incontestability-title',
  [Service.monitoringCaD]: 'legal-services-tm-c&d-title',
  [Service.monitoringOpposition]: 'legal-services-filing-opposition-title',
  [Service.officeActionDeadlineExtension]:
    'legal-services-tm-office-action-deadline-extension-title',
  [Service.declarationOfUse]: 'legal-services-tm-declaration-of-use-title',
  [Service.administrativeChange]: 'legal-services-administrative-change-title',
  [Service.monitoringMarketplaceCaD]: 'legal-services-monitoring-marketplace-c&d-letter-title',
  [Service.monitoringSocialMediaCaD]: 'legal-services-monitoring-social-media-c&d-letter-title',
  [Service.warningLetter]: 'legal-services-monitoring-warning-letter-title',
  [Service.caseSolution]: 'legal-services-case-solution',
  [Service.infringementTakedown]: 'legal-services-infringement-takedown-title',
  // only here for completeness as setting typing without it is pain
  [Service.tmRegistration]: 'legal-services-trademark-registration-title',
  [Service.brandMonitoring]: 'legal-services-brand-monitoring-title',
}

// const mapping
export const serviceInternalTypeToLabel = {
  [ServiceRequestInternalType.EUIPCase]: 'EU IP Case',
  [ServiceRequestInternalType.UKIPCase]: 'UK IP case',
  [ServiceRequestInternalType.USIPCase]: 'US IP case',
  [ServiceRequestInternalType.otherIPCase]: 'Other IP case',
  [ServiceRequestInternalType.IPCaseThroughPartner]: 'IP case through partner',
  [ServiceRequestInternalType.EUDiffOfficeAction]: 'EU diff office action',
  [ServiceRequestInternalType.UKDiffOfficeAction]: 'UK diff office action',
  [ServiceRequestInternalType.USDiffOfficeAction]: 'US diff office action',
  [ServiceRequestInternalType.AUNZDiffOfficeAction]: 'AUNZ diff office action',
  [ServiceRequestInternalType.otherDiffOfficeAction]: 'Other diff office action',
  [ServiceRequestInternalType.diffOfficeActionForPartner]: 'Diff office action for partner',
  [ServiceRequestInternalType.diffOfficeActionThroughPartner]: 'Diff office action through partner',
  [ServiceRequestInternalType.EUEasyOfficeAction]: 'EU easy office action',
  [ServiceRequestInternalType.UKEasyOfficeAction]: 'UK easy office action',
  [ServiceRequestInternalType.USEasyOfficeAction]: 'US easy office action',
  [ServiceRequestInternalType.AUNZEasyOfficeAction]: 'AUNZ easy office action',
  [ServiceRequestInternalType.otherEasyOfficeAction]: 'Other easy office action',
  [ServiceRequestInternalType.easyOfficeActionForPartner]: 'Easy office action for partner',
  [ServiceRequestInternalType.easyOfficeActionThroughPartner]: 'Easy office action through partner',

  [ServiceRequestInternalType.USStatementOfUse]: 'US SoU',
  [ServiceRequestInternalType.SoUForPartner]: 'SoU for partner',
  [ServiceRequestInternalType.USDeclarationOfUse]: 'US DoU',
  [ServiceRequestInternalType.DoUForPartner]: 'DoU for partner',

  [ServiceRequestInternalType.otherEasyFollowUp]: 'Other easy follow up',
  [ServiceRequestInternalType.caseAssessment]: 'Case assessment',
  [ServiceRequestInternalType.EUMonitoring]: 'EU monitoring',
  [ServiceRequestInternalType.UKMonitoring]: 'UK monitoring',
  [ServiceRequestInternalType.tmRenewal]: 'TM renewal',
  [ServiceRequestInternalType.tmAssignment]: 'TM assignment',
  [ServiceRequestInternalType.tmStrategy]: 'TM strategy',
  [ServiceRequestInternalType.extraChecksPackage1]: "5 additional lawyer's checks",
  [ServiceRequestInternalType.extraChecksPackage2]: "10 additional lawyer's checks",
  [ServiceRequestInternalType.tmConsultation]: 'TM consultation',
  [ServiceRequestInternalType.tmRepresentation]: 'TM representation',
  [ServiceRequestInternalType.tmWatch]: 'TM watch',
  [ServiceRequestInternalType.extraClasses]: 'Extra Classes',
  [ServiceRequestInternalType.customGSList]: 'Custom G&S list',
  [ServiceRequestInternalType.e2eProtection]: 'E2E Protection',
  [ServiceRequestInternalType.domainResolution]: 'Domain resolution',
  [ServiceRequestInternalType.b2bPartnership]: 'B2B partnership',
  [ServiceRequestInternalType.nonStandardCountryRegistration]: 'Non-standard country registration',
  [ServiceRequestInternalType.euipoGrant]: 'EUIPO grant',
  [ServiceRequestInternalType.caseSolution]: 'Case solution',
  [ServiceRequestInternalType.infringementTakedown]: 'Infringement takedown',
  [ServiceRequestInternalType.brandMonitoring]: 'Brand monitoring',

  [ServiceRequestInternalType.EUOfficeAction]: 'DEPRECATED - EU office action',
  [ServiceRequestInternalType.UKOfficeAction]: 'DEPRECATED - UK office action',
  [ServiceRequestInternalType.AUOfficeAction]: 'DEPRECATED - AU office action',
  [ServiceRequestInternalType.USLikelihoodOfConfusion]: 'DEPRECATED - US likelihood of confusion',
  [ServiceRequestInternalType.USOfficeAction]: 'DEPRECATED - US office action (LoC excl.)',
  [ServiceRequestInternalType.SKCZIPCase]: 'DEPRECATED - SK/CZ IP case',
  [ServiceRequestInternalType.SKCZOfficeAction]: 'DEPRECATED - SK/CZ office action',
  [ServiceRequestInternalType.CNOfficeAction]: 'DEPRECATED - China office action',
  [ServiceRequestInternalType.otherOfficeAction]: 'DEPRECATED - Other office action',
  [ServiceRequestInternalType.USOfficeActionDeadlineExtension]:
    'DEPRECATED - Office action deadline extension',
}

export const serviceInternalTypeToTitle = {
  [ServiceRequestInternalType.EUIPCase]: 'eu-ip-case-title',
  [ServiceRequestInternalType.UKIPCase]: 'uk-ip-case-title',
  [ServiceRequestInternalType.USIPCase]: 'us-ip-case-title',
  [ServiceRequestInternalType.otherIPCase]: 'other-ip-case-title',
  [ServiceRequestInternalType.IPCaseThroughPartner]: 'ip-case-through-partner-title',
  [ServiceRequestInternalType.EUDiffOfficeAction]: 'eu-office-action-title',
  [ServiceRequestInternalType.UKDiffOfficeAction]: 'uk-office-action-title',
  [ServiceRequestInternalType.USDiffOfficeAction]: 'us-office-action-title',
  [ServiceRequestInternalType.AUNZDiffOfficeAction]: 'aunz-office-action-title',
  [ServiceRequestInternalType.otherDiffOfficeAction]: 'other-office-action-title',
  [ServiceRequestInternalType.diffOfficeActionForPartner]: 'other-office-action-title',
  [ServiceRequestInternalType.diffOfficeActionThroughPartner]: 'other-office-action-title',
  [ServiceRequestInternalType.EUEasyOfficeAction]: 'eu-office-action-title',
  [ServiceRequestInternalType.UKEasyOfficeAction]: 'uk-office-action-title',
  [ServiceRequestInternalType.USEasyOfficeAction]: 'us-office-action-title',
  [ServiceRequestInternalType.AUNZEasyOfficeAction]: 'aunz-office-action-title',
  [ServiceRequestInternalType.otherEasyOfficeAction]: 'other-office-action-title',
  [ServiceRequestInternalType.easyOfficeActionForPartner]: 'other-office-action-title',
  [ServiceRequestInternalType.easyOfficeActionThroughPartner]: 'other-office-action-title',

  [ServiceRequestInternalType.USStatementOfUse]: 'us-statement-of-use-title',
  [ServiceRequestInternalType.SoUForPartner]: 'us-statement-of-use-title',
  [ServiceRequestInternalType.USDeclarationOfUse]: 'us-declaration-of-use-title',
  [ServiceRequestInternalType.DoUForPartner]: 'us-declaration-of-use-title',

  [ServiceRequestInternalType.otherEasyFollowUp]: 'other-easy-follow-up-title',
  [ServiceRequestInternalType.caseAssessment]: 'case-assessment-title',
  [ServiceRequestInternalType.EUMonitoring]: 'eu-monitoring-title',
  [ServiceRequestInternalType.UKMonitoring]: 'uk-monitoring-title',
  [ServiceRequestInternalType.tmRenewal]: 'tm-renewal-title',
  [ServiceRequestInternalType.tmAssignment]: 'tm-assignment-title',
  [ServiceRequestInternalType.tmStrategy]: 'tm-strategy-title',
  [ServiceRequestInternalType.extraChecksPackage1]: 'extra-checks-package-1-title',
  [ServiceRequestInternalType.extraChecksPackage2]: 'extra-checks-package-2-title',
  [ServiceRequestInternalType.tmConsultation]: 'tm-consultation-title',
  [ServiceRequestInternalType.tmRepresentation]: 'tm-representation-title',
  [ServiceRequestInternalType.tmWatch]: 'tm-watch-title',
  [ServiceRequestInternalType.extraClasses]: 'extra-classes-title',
  [ServiceRequestInternalType.customGSList]: 'custom-gs-list-title',
  [ServiceRequestInternalType.e2eProtection]: 'service-e2e-protection-title',
  [ServiceRequestInternalType.domainResolution]: 'domain-resolution-title',
  [ServiceRequestInternalType.b2bPartnership]: 'B2B-partnership-title',
  [ServiceRequestInternalType.nonStandardCountryRegistration]:
    'nonstandard-country-registration-title',
  [ServiceRequestInternalType.euipoGrant]: 'euipo-grant-title',
  [ServiceRequestInternalType.caseSolution]: 'case-solution-title',
  [ServiceRequestInternalType.infringementTakedown]: 'infringement-takedown-title',
  [ServiceRequestInternalType.brandMonitoring]: 'brand-monitoring-title',

  [ServiceRequestInternalType.USOfficeActionDeadlineExtension]:
    'us-office-action-deadline-extension-title',
  [ServiceRequestInternalType.EUOfficeAction]: 'eu-office-action-title',
  [ServiceRequestInternalType.UKOfficeAction]: 'uk-office-action-title',
  [ServiceRequestInternalType.USLikelihoodOfConfusion]: 'us-loc-title',
  [ServiceRequestInternalType.USOfficeAction]: 'us-office-action-title',
  [ServiceRequestInternalType.SKCZIPCase]: 'skcz-ip-case-title',
  [ServiceRequestInternalType.SKCZOfficeAction]: 'skcz-office-action-title',
  [ServiceRequestInternalType.AUOfficeAction]: 'au-office-action-title',
  [ServiceRequestInternalType.CNOfficeAction]: 'cn-office-action-title',
  [ServiceRequestInternalType.otherOfficeAction]: 'other-office-action-title',
}

export const serviceToInternalType = {
  [Service.tmAssignment]: ServiceRequestInternalType.tmAssignment,
  [Service.tmAssignmentAgreement]: ServiceRequestInternalType.tmAssignment,
  [Service.tmRenewal]: ServiceRequestInternalType.tmRenewal,
  [Service.tmStrategy]: ServiceRequestInternalType.tmStrategy,
  [Service.tmRepresentation]: ServiceRequestInternalType.tmRepresentation,
  [Service.b2bPartnership]: ServiceRequestInternalType.b2bPartnership,
  [Service.domainResolution]: ServiceRequestInternalType.domainResolution,
  [Service.tmWatch]: ServiceRequestInternalType.tmWatch,
  [Service.statementOfUse]: ServiceRequestInternalType.USStatementOfUse,
  [Service.declarationOfUse]: ServiceRequestInternalType.USDeclarationOfUse,
  [Service.extraChecksPackage1]: ServiceRequestInternalType.extraChecksPackage1,
  [Service.extraChecksPackage2]: ServiceRequestInternalType.extraChecksPackage2,
  [Service.declarationOfIncontestability]: null,
  [Service.officeAction]: null,
  [Service.filingOpposition]: null,
  [Service.cdLetter]: null,
  [Service.cdLetterResponse]: null,
  [Service.gsLimitation]: null,
  [Service.responseToObjection]: null,
  [Service.coexistenceAgreement]: null,
  [Service.tmSettlement]: null,
  [Service.enforcementOfRights]: null,
  [Service.officeActionDeadlineExtension]: null,
  [Service.caseSolution]: ServiceRequestInternalType.caseSolution,
  [Service.infringementTakedown]: ServiceRequestInternalType.infringementTakedown,
}

export const statusPriorities: Record<ServiceRequestStatus, number> = {
  [ServiceRequestStatus.dismissed]: 0,
  [ServiceRequestStatus.refunded]: 0,
  [ServiceRequestStatus.created]: 10,
  [ServiceRequestStatus.clientApproached]: 20,
  [ServiceRequestStatus.internalReviewRequested]: 30,
  [ServiceRequestStatus.moreInformationRequested]: 40,
  [ServiceRequestStatus.reviewCompleted]: 50,
  [ServiceRequestStatus.priceQuoteReady]: 60,
  [ServiceRequestStatus.paymentLinkSent]: 70,
  [ServiceRequestStatus.orderCompleted]: 80,
  [ServiceRequestStatus.paid]: 90,
  [ServiceRequestStatus.processed]: 100,
}

export const servicesForPriceList = {
  [LegalServiceGroups.simple]: [
    Service.tmRepresentation,
    Service.tmAssignment,
    Service.tmRenewal,
    Service.gsLimitation,
    Service.statementOfUse,
    Service.tmStrategy,
    Service.extraChecksPackage1,
    Service.extraChecksPackage2,
  ],
  [LegalServiceGroups.complex]: [
    Service.tmAssignmentAgreement,
    Service.cdLetter,
    Service.coexistenceAgreement,
    Service.cdLetterResponse,
    Service.tmSettlement,
    Service.officeAction,
    Service.responseToObjection,
  ],
}

export const legalServices: LegalServiceContent[] = [
  // registration services
  {
    key: Service.tmRegistration,
    title: 'legal-services-registration-title',
    description: 'legal-services-registration-desc',
    linkUrl: routeTo.verification.form(),
    hasCustomPage: true,
    processDetail: {
      category: LegalServiceCategory.registration,
      description: 'legal-services.service-registration-process-desc',
      buttonText: 'legal-services.service-registration-process-button',
    },
  },
  {
    key: Service.tmStrategy,
    title: 'legal-services-tm-strategy-title',
    description: 'legal-services-tm-strategy-desc',
    linkUrl: routeTo.trademarkStrategy(),
    group: LegalServiceGroups.simple,
    hasCustomPage: true,
    processDetail: {
      category: LegalServiceCategory.registration,
      title: 'legal-services.service-tm-strategy-process-title',
      description: 'legal-services.service-tm-strategy-process-desc',
      buttonText: 'learn-more-text',
    },
  },
  {
    key: Service.extraChecksPackage1,
    title: 'legal-services-extra-checks-package-1',
    description: 'legal-services-extra-checks-package-1-desc',
    group: LegalServiceGroups.simple,
    hasCustomPage: false,
  },
  {
    key: Service.extraChecksPackage2,
    title: 'legal-services-extra-checks-package-2',
    description: 'legal-services-extra-checks-package-2-desc',
    group: LegalServiceGroups.simple,
    hasCustomPage: false,
  },
  {
    key: 'free-consultation',
    title: 'legal-services-consultation-title',
    linkUrl: routeTo.bookConsultation(),
    hasCustomPage: true,
    processDetail: {
      category: LegalServiceCategory.registration,
      description: 'legal-services.service-consultation-process-desc',
      buttonText: 'legal-services.service-consultation-process-button',
    },
  },
  {
    key: 'tm-amazon',
    title: 'legal-services-amazon-brand-registry-title',
    linkUrl: routeTo.amazonBrandRegistry(),
    hasCustomPage: true,
  },

  // post-filing services
  {
    key: Service.tmRepresentation,
    title: 'legal-services-tm-representation-title',
    description: 'legal-services-tm-representation-desc',
    linkUrl: routeTo.tmRepresentation(),
    group: LegalServiceGroups.simple,
    hasCustomPage: true,
    processDetail: {
      category: LegalServiceCategory.postFiling,
      description: 'legal-services.service-tm-representation-process-desc',
      buttonText: 'learn-more-text',
    },
  },
  {
    key: Service.officeAction,
    title: 'legal-services-tm-office-action-title',
    description: 'legal-services-tm-office-action-desc',
    linkUrl: routeTo.officeActionResponse(),
    group: LegalServiceGroups.complex,
    hasCustomPage: true,
    processDetail: {
      category: LegalServiceCategory.postFiling,
      description: 'legal-services.service-tm-office-action-process-desc',
      buttonText: 'learn-more-text',
    },
  },
  {
    key: Service.responseToObjection,
    title: 'legal-services-tm-objection-title',
    description: 'legal-services-tm-objection-desc',
    linkUrl: routeTo.oppositionResponse(),
    group: LegalServiceGroups.complex,
    hasCustomPage: true,
    processDetail: {
      category: LegalServiceCategory.postFiling,
      description: 'legal-services.service-tm-objection-process-desc',
      buttonText: 'learn-more-text',
    },
  },
  {
    key: Service.cdLetterResponse,
    title: 'legal-services-tm-c&d-response-title',
    description: 'legal-services-tm-c&d-response-desc',
    linkUrl: routeTo.ceaseAndDesistLetterResponse(),
    group: LegalServiceGroups.complex,
    hasCustomPage: true,
    processDetail: {
      category: LegalServiceCategory.postFiling,
      description: 'legal-services.service-tm-c&d-response-process-desc',
      buttonText: 'learn-more-text',
    },
  },
  {
    key: Service.coexistenceAgreement,
    title: 'legal-services-tm-coexistence-title',
    description: 'legal-services-tm-coexistence-desc',
    linkUrl: routeTo.coexistenceAgreement(),
    group: LegalServiceGroups.complex,
    hasCustomPage: true,
    processDetail: {
      category: LegalServiceCategory.postFiling,
      description: 'legal-services.service-tm-coexistence-process-desc',
      buttonText: 'learn-more-text',
    },
  },
  {
    key: Service.gsLimitation,
    title: 'legal-services-tm-limitation-title',
    description: 'legal-services-tm-limitation-desc',
    linkUrl: routeTo.limitationOfGoodsAndServices(),
    group: LegalServiceGroups.simple,
    hasCustomPage: true,
    processDetail: {
      category: LegalServiceCategory.postFiling,
      description: 'legal-services.service-tm-limitation-process-desc',
      buttonText: 'learn-more-text',
    },
  },

  // maintenance services
  {
    key: Service.tmRenewal,
    title: 'legal-services-tm-renewal-title',
    description: 'legal-services-tm-renewal-desc',
    linkUrl: routeTo.trademarkRenewal.home(),
    group: LegalServiceGroups.simple,
    hasCustomPage: true,
    processDetail: {
      category: LegalServiceCategory.maintenance,
      description: 'legal-services.service-tm-renewal-process-desc',
      buttonText: 'legal-services.service-tm-renewal-process-button',
    },
  },
  {
    key: Service.tmAssignmentAgreement,
    title: 'legal-services-ownership-transfer-title',
    description: 'legal-services-ownership-transfer-desc',
    linkUrl: routeTo.trademarkOwnershipTransfer.home(),
    group: LegalServiceGroups.complex,
    hasCustomPage: true,
    processDetail: {
      category: LegalServiceCategory.maintenance,
      description: 'legal-services.service-ownership-transfer-process-desc',
      buttonText: 'legal-services.service-ownership-transfer-process-button',
    },
  },
  {
    key: Service.statementOfUse,
    title: 'legal-services-tm-statement-of-use-title',
    description: 'legal-services-tm-statement-of-use-desc',
    linkUrl: routeTo.statementOfUse(),
    group: LegalServiceGroups.simple,
    hasCustomPage: true,
    processDetail: {
      category: LegalServiceCategory.maintenance,
      description: 'legal-services.service-tm-statement-of-use-process-desc',
      buttonText: 'legal-services.service-tm-statement-of-use-process-button',
    },
  },
  {
    key: Service.declarationOfUse,
    title: 'legal-services-tm-declaration-of-use-title',
    linkUrl: routeTo.declarationOfUse(),
    hasCustomPage: true,
    processDetail: {
      category: LegalServiceCategory.maintenance,
      description: 'legal-services.service-tm-declaration-of-use-process-desc',
      buttonText: 'legal-services.service-tm-declaration-of-use-process-button',
    },
  },
  {
    key: Service.declarationOfIncontestability,
    title: 'legal-services-tm-declaration-of-incontestability-title',
    description: 'legal-services-tm-declaration-of-incontestability-desc',
    linkUrl: routeTo.legalServices(),
    hasCustomPage: false,
    processDetail: {
      category: LegalServiceCategory.maintenance,
      description: 'legal-services.service-tm-declaration-of-incontestability-process-desc',
      buttonText: 'legal-services.service-tm-declaration-of-incontestability-process-button',
    },
  },

  // infringement services
  {
    key: Service.brandMonitoring,
    title: 'legal-services-brand-monitoring-title',
    linkUrl: routeTo.brandMonitoring.home(),
    hasCustomPage: true,
    processDetail: {
      category: LegalServiceCategory.infringement,
      description: 'legal-services.service-brand-monitoring-process-desc',
      buttonText: 'learn-more-text',
    },
  },
  {
    key: Service.infringementTakedown,
    title: 'legal-services-infringement-takedown-title',
    linkUrl: routeTo.reportBrandInfringement(),
    hasCustomPage: true,
    processDetail: {
      category: LegalServiceCategory.infringement,
      description: 'legal-services.service-infringement-takedown-process-desc',
      buttonText: 'learn-more-text',
    },
  },
  {
    key: Service.filingOpposition,
    title: 'legal-services-filing-opposition-title',
    description: 'legal-services-filing-opposition-desc',
    linkUrl: routeTo.filingOpposition(),
    hasCustomPage: true,
    processDetail: {
      category: LegalServiceCategory.infringement,
      description: 'legal-services.service-filing-opposition-process-desc',
      buttonText: 'learn-more-text',
    },
  },
  {
    key: Service.cdLetter,
    title: 'legal-services-tm-c&d-title',
    description: 'legal-services-tm-c&d-desc',
    linkUrl: routeTo.ceaseAndDesistLetter(),
    group: LegalServiceGroups.complex,
    hasCustomPage: true,
    processDetail: {
      category: LegalServiceCategory.infringement,
      description: 'legal-services.service-tm-c&d-process-desc',
      buttonText: 'learn-more-text',
    },
  },
  {
    key: Service.enforcementOfRights,
    title: 'legal-services-enforcement-of-rights-title',
    linkUrl: routeTo.legalServices(),
    hasCustomPage: false,
    processDetail: {
      category: LegalServiceCategory.infringement,
      description: 'legal-services.service-enforcement-of-rights-process-desc',
      buttonText: 'button-contact-us',
    },
  },
  {
    key: Service.tmAssignment,
    title: 'legal-services-tm-assignment-title',
    description: 'legal-services-tm-assignment-desc',
    linkUrl: routeTo.legalServices(),
    group: LegalServiceGroups.simple,
    hasCustomPage: false,
  },
  {
    key: Service.tmSettlement,
    title: 'legal-services-tm-settlement-title',
    description: 'legal-services-tm-settlement-desc',
    linkUrl: routeTo.legalServices(),
    group: LegalServiceGroups.complex,
    hasCustomPage: false,
  },
]

const filterLegalServicesByCategory = (category: LegalServiceCategory) =>
  legalServices.filter((service) => service.processDetail?.category === category)

export const registrationLegalServices = filterLegalServicesByCategory(
  LegalServiceCategory.registration,
)
export const postFilingLegalServices = filterLegalServicesByCategory(
  LegalServiceCategory.postFiling,
)
export const maintenanceLegalServices = filterLegalServicesByCategory(
  LegalServiceCategory.maintenance,
)
export const infringementLegalServices = filterLegalServicesByCategory(
  LegalServiceCategory.infringement,
)
